import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PrimaryButton from "../components/PrimaryButton";
import useDataFetch from "../hooks/useDataFetch";
import Dropdown from "../components/Dropdown.js";
import { MobileResponsive } from "./MobileResponsive.jsx";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker.jsx";
import { InfoWindowPopup } from "../components/InfoWindow.jsx";
import { getEntityData } from "../utils/mapUtils.js";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUnsubscribe,
  getDriverCoordinates,
} from "../features/driverSlice.js";
import DynamicMetaTags from "../components/DynamicMetaTags.js";
import Avatar from "react-avatar";

const Drivers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = searchParams.get("st");

  const [currentPage, setCurrentPage] = useState(1);
  const [searchName, setSearchName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(
    params === "active" ? "active" : ""
  );

  const [inputStates, setInputStates] = useState({
    searchDriver: { isFocused: false, isFilled: false, value: "" },
  });

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage);
    if (searchName.trim()) params.append("name", searchName.trim());
    if (selectedStatus) params.append("status", selectedStatus);
    return `/admin/drivers-list?${params.toString()}`;
  };

  const { data, isLoading } = useDataFetch(buildApiUrl());

  const { data: newDrivers } = useDataFetch(
    "/admin/onboarding-new-drivers-list"
  );

  // fetch driver coordinates from redux
  const driverCoordinates = useSelector(
    (state) => state.drivers.driverCoordinates
  );

  useEffect(() => {
    dispatch(getDriverCoordinates(process.env.REACT_APP_COLLECTION_DRIVER));

    return () => {
      dispatch(clearUnsubscribe());
    };
  }, [dispatch]);

  const drivers = data?.data || [];
  const totalDriversCount = data?.totalDriversCount || 0;
  const activeDriversCount = data?.activeDriversCount || 0;
  const totalPages = data?.totalPages || 1;

  const getDriverId = (driver) => driver?._id || driver?.id;

  // Filter driver coordinates to match the displayed drivers
  const filteredDriverCoordinates = driverCoordinates.filter((coordDriver) =>
    drivers.some((driver) => getDriverId(driver) === coordDriver.id)
  );

  // Create a mapping of driver IDs to their position in the list
  const driverNumberMapping = {};
  drivers.forEach((driver, index) => {
    driverNumberMapping[getDriverId(driver)] = (currentPage - 1) * 15 + index + 1;
  });

  // Add driver numbers to the coordinates data
  const driverCoordinatesWithNumbers = filteredDriverCoordinates.map(
    (coordDriver) => ({
      ...coordDriver,
      driverNumber: driverNumberMapping[coordDriver.id] || 1,
    })
  );

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker, newDrivers]);

  const handleInputChange = (event, inputField) => {
    const value = event.target.value;

    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: {
        ...prevState[inputField],
        value: value,
        isFilled: value !== "",
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchName(inputStates.searchDriver.value.trim());
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setInputStates((prevState) => ({
      ...prevState,
      searchDriver: { ...prevState.searchDriver, value: "", isFilled: false },
    }));
    setSearchName("");
    setCurrentPage(1);
  };

  const handleStatusChange = (option) => {
    const status = option === "Active Drivers" ? "active" : "";
    setSelectedStatus(status);
    setCurrentPage(1);
    if (status === "active") {
      setSearchParams("st=active");
    } else {
      setSearchParams("");
    }
  };

  const handleFocus = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: true },
    }));
  };

  const handleBlur = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: false },
    }));
  };

  const handleDriverClick = (driverId) => {
    navigate(`/drivers/${driverId}`);
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Optimize Your Driver Management"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary flex items-start">
          <div className="flex flex-col w-full">
            <div className="flex justify-between w-[95.5%]">
              <div className="flex items-center gap-3 cursor-default">
                <span className="font-semibold text-[20px]">
                  {selectedStatus === "active"
                    ? `Active Drivers: ${activeDriversCount}`
                    : `Total Drivers: ${totalDriversCount}`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <form
                  action=""
                  className="flex items-center gap-2"
                  onSubmit={handleSubmit}
                >
                  <div className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] 2xl:w-[20vw] xl:w-[16vw] lg:w-[16vw]">
                    <div className="relative w-full">
                      <input
                        type="text"
                        placeholder="Search driver name"
                        className={`peer rounded-full 2xl:py-1.5 xl:py-1 lg:py-0 text-[#F7631D] placeholder:text-transparent 2xl:text-base xl:text-base lg:text-xs font-medium w-full outline-none px-1 placeholder:select-none`}
                        value={inputStates.searchDriver.value}
                        onInput={(e) => handleInputChange(e, "searchDriver")}
                        onFocus={() => handleFocus("searchDriver")}
                        onBlur={() => handleBlur("searchDriver")}
                      />
                      <label
                        className={`absolute left-1 transform transition-all duration-200 ${inputStates.searchDriver.isFocused ||
                          inputStates.searchDriver.isFilled
                          ? "-top-0.5 -translate-y-1/2 2xl:left-2 xl:left-1 lg:left-0  2xl:text-xs xl:text-[11px] lg:text-[9px] px-1 bg-white rounded-full"
                          : "top-1/2 -translate-y-1/2 2xl:left-3 xl:left-1 lg:left-0.5 2xl:text-base xl:text-[14px] lg:text-xs font-medium"
                          } text-[#F7631D] pointer-events-none`}
                      >
                        Search driver name
                      </label>
                    </div>
                    {inputStates.searchDriver.value && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="text-[#F7631D] hover:text-[#d55419] text-xl"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-[#F7631D] text-white px-4 py-1.5 rounded-full font-semibold hover:bg-[#d55419] transition-colors flex items-center gap-2 2xl:text-base xl:text-sm lg:text-xs"
                  >
                    <img
                      src="/assets/search.svg"
                      alt=""
                      className="2xl:w-4 2xl:h-4 xl:w-3 xl:h-3 lg:w-3 lg:h-3 brightness-0 invert"
                    />
                    Search
                  </button>
                </form>
              </div>
              <div className="flex items-center gap-4 text-[#413F3F]">
                <Dropdown
                  options={["Active Drivers", "Total Drivers"]}
                  selected={
                    selectedStatus === "active"
                      ? "Active Drivers"
                      : "Total Drivers"
                  }
                  onSelect={handleStatusChange}
                  label="Select Option"
                  mutation={true}
                />
              </div>
            </div>

            <div
              className={`grid ${isLoading ? "grid-cols-1" : "grid-cols-3"
                } gap-x-3 gap-y-6 w-[95.5%] py-8`}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
                  <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
                  <p className="text-gray-600 font-medium">Loading..</p>
                </div>
              ) : drivers.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
                  <p className="text-gray-600 font-medium">No drivers found</p>
                </div>
              ) : (
                drivers.map((driver, index) => (
                  <div
                    key={driver?._id}
                    className="bg-[rgb(255,255,255)] rounded-lg flex items-center justify-between border-[0.5px] border-[#00000014] shadow-custom4 px-2 pl-4 py-1.5 cursor-pointer relative overflow-hidden"
                    onClick={() => handleDriverClick(driver?._id)}
                  >
                    <div className="absolute -top-[34px] -right-[34px] w-16 h-16 bg-gradient-to-r from-[#F75E00] to-[#FF8A3D] rotate-45 shadow-lg transform group-hover:scale-110 transition-transform">
                      <div className="absolute bottom-1 left-0 right-0 text-white font-bold text-xs flex justify-center items-center rotate-[315deg]">
                        {(currentPage - 1) * 15 + index + 1}
                      </div>
                    </div>

                    <div className="flex items-center 2xl:gap-4 xl:gap-3 lg:gap-3">
                      {/* <img
                          src="/assets/user.png"
                          alt={driver?.name}
                          className="2xl:h-14 xl:h-12 lg:h-12"
                        /> */}
                      {driver?.driver_image ? (
                        driver?.driver_image?.includes("img.freepik.com") ? (
                          <Avatar
                            name={driver?.name}
                            size="52"
                            round={true}
                            maxInitials={2}
                            textSizeRatio={2.5}
                            style={{ letterSpacing: "1px" }}
                          />
                        ) : (
                          <img
                            src={driver?.driver_image}
                            alt=""
                            className="h-[52px] rounded-full"
                          />
                        )
                      ) : (
                        <Avatar
                          name={driver?.name}
                          size="52"
                          round={true}
                          maxInitials={2}
                          textSizeRatio={2.5}
                          style={{ letterSpacing: "1px" }}
                        />
                      )}

                      <div className="flex flex-col 2xl:gap-1.5 xl:gap-1.5 lg:gap-1.5">
                        <p className="2xl:text-[16px] xl:text-[13px] lg:text-[12px] font-semibold tracking-wide capitalize">
                          {driver?.name}
                        </p>
                        <p
                          className={`2xl:text-[13px] xl:text-[10px] lg:text-[9px] ${driver?.is_verified
                              ? driver?.activeForOrders === "on" &&
                                !driver?.onBreak
                                ? "text-[#25AD34]"
                                : driver?.activeForOrders === "off" &&
                                  driver?.onBreak
                                  ? "text-[#F9C45D]"
                                  : "text-[#F34B50]"
                              : "text-[#F34B50]"
                            } font-semibold tracking-wide flex items-center gap-0.5 h-1 2xl:mb-2.5 xl:mb-1.5 lg:mb-1`}
                        >
                          <span className="2xl:pt-[3px] xl:pt-[1.5px] lg:pt-[0.5px]">
                            {driver?.is_verified
                              ? driver?.activeForOrders === "on" &&
                                !driver?.onBreak
                                ? "Online"
                                : driver?.activeForOrders === "off" &&
                                  driver?.onBreak
                                  ? "On a break"
                                  : "Offline"
                              : "Not verified"}
                          </span>
                          <span className="2xl:text-3xl xl:text-[19px] lg:text-[15px]">
                            •
                          </span>
                        </p>
                      </div>
                    </div>
                    <img
                      src="/assets/right-arrow.svg"
                      alt=""
                      className="h-[26px]"
                    />
                  </div>
                ))
              )}
            </div>

            {/* Pagination Component */}
            <div className="w-[95.5%] flex justify-center">
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-[#F7631D] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-[#F7631D] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {isLoading ? (
              ""
            ) : (
              <div className="w-[95.5%] flex justify-center py-10">
                <PrimaryButton
                  text={`New driver requests (${newDrivers?.totalCount || 0})`}
                  uppercase={false}
                  path="/manage-drivers"
                  width="2xl:w-[30%] xl:w-[35%] lg:w-[40%]"
                />
              </div>
            )}
          </div>

          <div className="w-[48%] h-screen">
            <div className="2xl:w-[100%] xl:w-full lg:w-full md:w-full sm:w-full h-[92%] rounded-xl flex justify-center items-center overflow-hidden shadow-default">
              {isLoading ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              ) : (
                <Map
                  mapId={"terrain"}
                  defaultZoom={6}
                  defaultCenter={{
                    lat: 54.5,
                    lng: -3.0,
                  }}
                  gestureHandling={"greedy"}
                  onClick={onMapClick}
                  disableDefaultUI
                  styles={"terrain"}
                  fullscreenControl={true}
                  mapTypeId={"terrain"}
                  isFractionalZoomEnabled={true}
                  mapTypeControlOptions
                  zoomControl
                  scaleControl
                  streetViewControl
                  mapTypeControl
                  cameraControl
                  rotateControl
                >
                  <MapMarker
                    data={driverCoordinatesWithNumbers}
                    entityType="drivers"
                    onMouseEnter={handleMarkerClick}
                  />

                  <InfoWindowPopup
                    infoWindowShown={infoWindowShown}
                    selectedMarker={selectedMarker}
                    handleCloseInfoWindow={handleCloseInfoWindow}
                  />
                </Map>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Drivers;
